import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, StickyNote, Trash2, Pencil, User, Calendar, Check, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => format(new Date(), 'yyyy-MM-dd');
const fmtDate = (d) => { try { return format(parseISO(d), 'dd MMMM yyyy', { locale: fr }); } catch { return d; } };

export const NotesPanel = ({ dossierId, candidatId = null, title = 'Notes & observations', testId = 'notes-panel' }) => {
  const { user } = useAuth();
  const defaultAuthor = `${user?.nom || ''} ${user?.prenom || ''}`.trim() || user?.email || '';
  const isAdmin = ['admin', 'super_admin', 'responsable_pedagogique', 'administratif'].includes(user?.role);
  const [notes, setNotes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date_redaction: today(), redacteur_nom: defaultAuthor, texte: '' });
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const fetchNotes = useCallback(async () => {
    try {
      const params = new URLSearchParams({ dossier_id: dossierId });
      if (candidatId) params.set('candidat_id', candidatId);
      const resp = await axios.get(`${API_URL}/notes?${params.toString()}`);
      setNotes(resp.data);
    } catch {}
  }, [dossierId, candidatId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  useEffect(() => { setForm((f) => ({ ...f, redacteur_nom: f.redacteur_nom || defaultAuthor })); }, [defaultAuthor]);

  const handleAdd = async () => {
    if (!form.texte.trim()) { toast.error('Saisissez une note'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/notes`, { dossier_id: dossierId, candidat_id: candidatId, ...form });
      setForm({ date_redaction: today(), redacteur_nom: defaultAuthor, texte: '' });
      setShowForm(false);
      fetchNotes();
      toast.success('Note ajoutée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const handleSaveEdit = async (id) => {
    if (!editText.trim()) { toast.error('La note est vide'); return; }
    try {
      await axios.put(`${API_URL}/notes/${id}`, { texte: editText });
      setEditingId(null);
      fetchNotes();
      toast.success('Note modifiée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/notes/${id}`);
      fetchNotes();
      toast.success('Note supprimée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const canEdit = (n) => isAdmin || n.redacteur_id === user?.id;

  return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-amber-500" />
            {title}
            <span className="text-xs font-normal text-slate-400 ml-1">({notes.length})</span>
          </h2>
          <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => setShowForm(!showForm)} data-testid={`${testId}-add-btn`}>
            <Plus className="w-3 h-3 mr-1" /> Nouvelle note
          </Button>
        </div>

        {showForm && (
          <div className="border border-amber-200 rounded-lg p-3 mb-4 bg-amber-50/40 space-y-3" data-testid={`${testId}-form`}>
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
              <div>
                <Label className="text-xs text-slate-500">Date de rédaction</Label>
                <Input type="date" value={form.date_redaction} onChange={(e) => setForm({ ...form, date_redaction: e.target.value })} className="h-8 text-sm" data-testid={`${testId}-date`} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Rédacteur</Label>
                <Input value={form.redacteur_nom} onChange={(e) => setForm({ ...form, redacteur_nom: e.target.value })} className="h-8 text-sm" data-testid={`${testId}-author`} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Note / observation</Label>
              <Textarea value={form.texte} onChange={(e) => setForm({ ...form, texte: e.target.value })} rows={3} placeholder="Votre observation..." className="text-sm" data-testid={`${testId}-text`} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={() => setShowForm(false)}>Annuler</Button>
              <Button type="button" size="sm" className="text-xs bg-amber-600 hover:bg-amber-700" onClick={handleAdd} disabled={saving} data-testid={`${testId}-save`}>Enregistrer</Button>
            </div>
          </div>
        )}

        {notes.length === 0 ? (
          <p className="text-xs text-slate-400 italic" data-testid={`${testId}-empty`}>Aucune note pour le moment.</p>
        ) : (
          <div className="space-y-2">
            {notes.map((n) => (
              <div key={n.id} className="border rounded-lg p-3 bg-white hover:bg-slate-50/60 transition-colors" data-testid={`note-${n.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-3 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(n.date_redaction)}</span>
                    <span className="inline-flex items-center gap-1 font-medium text-slate-700"><User className="w-3 h-3" /> {n.redacteur_nom}</span>
                  </div>
                  {canEdit(n) && editingId !== n.id && (
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => { setEditingId(n.id); setEditText(n.texte); }} className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" title="Modifier" data-testid={`note-edit-${n.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button type="button" onClick={() => handleDelete(n.id)} className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" title="Supprimer" data-testid={`note-delete-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
                {editingId === n.id ? (
                  <div className="space-y-2">
                    <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} className="text-sm" data-testid={`note-edit-text-${n.id}`} />
                    <div className="flex justify-end gap-1">
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditingId(null)}><X className="w-3 h-3 mr-1" /> Annuler</Button>
                      <Button type="button" size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={() => handleSaveEdit(n.id)} data-testid={`note-edit-save-${n.id}`}><Check className="w-3 h-3 mr-1" /> Enregistrer</Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.texte}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
